import {
  GeneratePostRequestHookStyle,
  GeneratePostRequestHumorLevel,
  GeneratePostRequestPostStyle,
  GeneratePostRequestSpacingStyle,
} from "@workspace/api-client-react";

export const PLATFORM_CONFIG = {
  twitter:   { label: "Twitter/X",  color: "bg-sky-500/10 border-sky-500/30 text-sky-400",     charLimit: 280   },
  linkedin:  { label: "LinkedIn",   color: "bg-blue-600/10 border-blue-600/30 text-blue-400",  charLimit: 3000  },
  reddit:    { label: "Reddit",     color: "bg-orange-500/10 border-orange-500/30 text-orange-400", charLimit: 40000 },
  instagram: { label: "Instagram",  color: "bg-pink-500/10 border-pink-500/30 text-pink-400",  charLimit: 2200  },
  facebook:  { label: "Facebook",   color: "bg-indigo-500/10 border-indigo-500/30 text-indigo-400", charLimit: 63206 },
};

export const PLATFORM_LENGTH_HINTS: Record<string, string> = {
  twitter:   "280 chars max · ~40 words",
  linkedin:  "150–250 words ideal",
  reddit:    "Any length — detail is rewarded",
  instagram: "Up to 2,200 chars",
  facebook:  "Any length — 40–80 words gets best reach",
};

export const PLATFORM_LABELS: Record<string, string> = {
  twitter: "Twitter / X",
  linkedin: "LinkedIn",
  reddit: "Reddit",
  instagram: "Instagram",
  facebook: "Facebook",
};

export const PLATFORM_SETTING_DESCRIPTIONS: Record<string, Record<string, string>> = {
  twitter: {
    spacing: "On Twitter/X, spacing is a punchline tool — each line break is a beat of silence before the reveal.",
    hook: "Your hook IS the post on Twitter. The first 5 words decide if anyone reads the rest.",
    humor: "A touch of wit thrives on Twitter. Deadpan and playful land best in 280 chars.",
    audience: "Twitter/X rewards broad, punchy takes over niche professional content.",
    emoji: "Emojis on Twitter are optional — use them only if they add visual rhythm.",
    postStyle: "Short-form styles (Hot Take, Question) tend to outperform long Storytelling on Twitter.",
  },
  linkedin: {
    spacing: "LinkedIn readers scroll fast — line breaks create breathing room and boost readability.",
    hook: "Your first line appears before 'see more'. Make it impossible to ignore.",
    humor: "LinkedIn appreciates dry wit and warmth, but full playfulness can undermine credibility.",
    audience: "LinkedIn's core audience is professionals. Executive and Professional presets perform well.",
    emoji: "Minimal emojis signal credibility on LinkedIn. Natural works well for approachable posts.",
    postStyle: "Storytelling, Factual, and How-To formats perform consistently well on LinkedIn.",
  },
  reddit: {
    spacing: "Reddit readers appreciate natural, conversational paragraphs — avoid over-engineered structure.",
    hook: "Reddit hooks work best when they feel genuine, not crafted. Lead with the real thing.",
    humor: "Reddit rewards wit and self-awareness. Dry wit and playful tones fit well.",
    audience: "Reddit communities are self-directed — General audience usually works best.",
    emoji: "Emojis are generally off-brand for Reddit's text-first culture.",
    postStyle: "Personal and Hot Take styles resonate with Reddit's community-first vibe.",
  },
  instagram: {
    spacing: "Instagram captions read in short bursts — spaced lines improve scannability on mobile.",
    hook: "Only the first line shows before 'more'. Lead with emotion or a compelling question.",
    humor: "Playful and witty tones work well on Instagram's expressive, visual-first platform.",
    audience: "Instagram skews toward community and general audiences. Keep it accessible.",
    emoji: "Emojis are at home on Instagram — Natural or Expressive both work well.",
    postStyle: "Storytelling and Personal styles consistently drive engagement on Instagram.",
  },
  facebook: {
    spacing: "Facebook posts read better with natural paragraph breaks — write like you're talking to friends.",
    hook: "Open with a scene or a direct question — Facebook rewards warmth and authenticity.",
    humor: "A light, warm tone works well on Facebook's community-oriented platform.",
    audience: "Facebook skews toward Community and General. Keep content approachable.",
    emoji: "Natural emoji use fits Facebook's casual, conversational tone.",
    postStyle: "Personal, Storytelling, and How-To formats get the most engagement on Facebook.",
  },
};

type StyleSuggestion = {
  hook?: GeneratePostRequestHookStyle;
  spacing?: GeneratePostRequestSpacingStyle;
  humor?: GeneratePostRequestHumorLevel;
};

export const STYLE_SUGGESTIONS: Partial<Record<GeneratePostRequestPostStyle, StyleSuggestion>> = {
  listicle:     { spacing: "spaced",  hook: "statistic" },
  storytelling: { spacing: "spaced",  hook: "anecdote",       humor: "dry-wit" },
  "hot-take":   { spacing: "tight",   hook: "bold-statement",  humor: "witty" },
  question:     { hook: "question" },
  "how-to":     { spacing: "spaced",  hook: "statistic" },
  factual:      { hook: "statistic",  humor: "none" },
  personal:     { hook: "anecdote",   humor: "dry-wit",        spacing: "spaced" },
  industry:     { hook: "bold-statement", humor: "none" },
};

export type ConflictWarning = { fields: string[]; message: string };

export function extractErrorMessage(err: Error, fallback: string): string {
  if (err?.message && err.message.length > 0 && err.message !== "[object Object]") {
    return err.message;
  }
  return fallback;
}
